import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, Platform, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Header, FormHeader, ScreenLayout, colors, spacing, typography } from '../components';
import { apiService } from '../services/api';
import type { AiStatus, AiModelChoice, AiTestResult } from '../services/ai.types';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { usePermissions } from '../hooks/usePermissions';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useGoBack } from '../hooks/useGoBack';
import { createTabPressHandler } from '../navigation/handleTabPress';

export const AiSetupScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { t, isRTL } = useLocale();
  const { navigateTo, activeTab, setActiveTab, currentScreen, routeParams } = useAppContext();
  const businessId = useAppStore(s => s.currentBusiness?._id);
  const role = useAppStore(s => s.businessRole);
  const { hasPermissionAtLeast, refreshPermissions } = usePermissions();
  const canRead = role === 'owner' || role === 'admin' || hasPermissionAtLeast('ai.manageConfig', 'view');
  const canEdit = role === 'owner' || role === 'admin' || hasPermissionAtLeast('ai.manageConfig', 'edit');
  const canViewUsage = role === 'owner' || role === 'admin' || hasPermissionAtLeast('ai.viewUsage', 'view');
  const { refresh: refreshProfile } = useProfileCompletion();
  const goBack = useGoBack('settings');
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [models, setModels] = useState<AiModelChoice[]>([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [test, setTest] = useState<AiTestResult | null>(null);
  const [knowledge, setKnowledge] = useState('');
  const [knowledgeState, setKnowledgeState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const currentBusiness = useRef(businessId);
  currentBusiness.current = businessId;
  const loadVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    const current = () => currentBusiness.current === businessId && loadVersion.current === version;
    setLoading(true); setError('');
    try {
      const response = await apiService.getAiStatus();
      if (!current()) return;
      if (response.statusCode !== 1000 || !response.data) throw new Error(t('ai.loadFailed'));
      setStatus(response.data);
      setKnowledge(response.data.config?.knowledge || ''); setKnowledgeState('idle');
      if (response.data.entitled) {
        const available = await apiService.getAiModels();
        if (!current()) return;
        if (available.statusCode !== 1000 || !available.data) throw new Error(t('ai.loadFailed'));
        setModels(available.data.models);
        setSelected(response.data.config?.modelKey || available.data.models.find(m => m.available)?.key || '');
      } else { setModels([]); setSelected(''); }
    } catch (err: any) { if (current()) setError(err.message || t('ai.loadFailed')); }
    finally { if (current()) setLoading(false); }
  }, [businessId, t]);

  useEffect(() => { void refreshPermissions(); }, [businessId]);
  useEffect(() => {
    setStatus(null); setModels([]); setTest(null);
    setBusy(false);
    if (canRead && currentScreen === 'ai-setup') void load(); else setLoading(false);
    return () => { loadVersion.current++; };
  }, [businessId, canRead, currentScreen, load]);

  const act = async (kind: 'connect' | 'test' | 'disconnect') => {
    setBusy(true); setError(''); setTest(null);
    const current = () => currentBusiness.current === businessId;
    try {
      if (kind === 'disconnect') {
        const response = await apiService.disconnectAiModel();
        if (!current()) return;
        if (response.statusCode !== 1000) throw new Error(t('ai.actionFailed'));
        await load(); await refreshProfile();
      } else if (kind === 'test') {
        const response = await apiService.testAiModel(selected);
        if (!current()) return;
        if (response.statusCode !== 1000 || !response.data) throw new Error(t('ai.actionFailed'));
        setTest(response.data);
      } else {
        const response = await apiService.connectAiModel(selected);
        if (!current()) return;
        if (response.statusCode !== 1000 || !response.data) throw new Error(t('ai.actionFailed'));
        setStatus({ entitled: true, configured: true, config: response.data.config });
        setTest(response.data.test);
        await refreshProfile();
      }
    } catch (err: any) { if (current()) setError(err.message || t('ai.actionFailed')); }
    finally { if (current()) setBusy(false); }
  };
  const rowDirection = isRTL ? 'row-reverse' : 'row';
  const textAlign = isRTL ? 'right' : 'left';
  return (
    <ScreenLayout header={<Header showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={createTabPressHandler(navigateTo, setActiveTab)} />} showBottomTabs={Platform.OS !== 'web'}>
      <FormHeader title={t('ai.setupTitle')} onBack={goBack} actions={canViewUsage ? [{ icon: 'bar-chart-outline', label: t('ai.historyTitle'), onPress: () => navigateTo('ai-usage') }] : []} />
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={[styles.subtitle, { textAlign }]}>{t('ai.setupDescription')}</Text>
        {!canRead ? <Text style={styles.error}>{t('ai.accessDenied')}</Text> : <>
          {!!error && <Text accessibilityRole="alert" style={styles.error}>{error}</Text>}
          {loading ? <ActivityIndicator color={colors.primary as string} /> : status && !status.entitled ? <View style={styles.card}>
            <Text style={styles.heading}>{t('ai.notIncluded')}</Text>
            <Text style={styles.subtitle}>{t('ai.notIncludedDescription')}</Text>
            <TouchableOpacity style={styles.primary} onPress={() => navigateTo('subscription')}><Text style={styles.primaryText}>{t('ai.viewPlan')}</Text></TouchableOpacity>
          </View> : <>
            <View style={styles.card}>
              <Text style={[styles.heading, { textAlign }]}>{status?.configured ? t('ai.connected') : t('ai.notConnected')}</Text>
              {status?.configured && <Text style={[styles.subtitle, { textAlign }]}>{status.config?.provider} · {status.config?.model}</Text>}
              <Text style={[styles.subtitle, { textAlign }]}>{t('ai.selectModel')}</Text>
              {!models.length && <Text style={styles.subtitle}>{t('ai.noModels')}</Text>}
              {models.map(model => <TouchableOpacity key={model.key} accessibilityRole="radio" accessibilityState={{ checked: selected === model.key, disabled: !model.available || busy || !canEdit }} disabled={!model.available || busy || !canEdit}
                onPress={() => setSelected(model.key)} style={[styles.model, { flexDirection: rowDirection }, selected === model.key && styles.selected]}>
                <Ionicons name={selected === model.key ? 'radio-button-on' : 'radio-button-off'} size={22} color={colors.primary as string} />
                <View style={{ flex: 1 }}><Text style={[styles.heading, { textAlign }]}>{model.name}</Text><Text style={[styles.subtitle, { textAlign }]}>{model.provider} · {model.model}</Text>
                  <Text style={[styles.subtitle, { textAlign }]}>{t(`ai.source.${model.source}`)}{!model.available ? ` · ${t('ai.unavailable')}` : ''}</Text></View>
              </TouchableOpacity>)}
              <TouchableOpacity disabled={busy} style={styles.secondary} onPress={() => void load()}><Text style={styles.link}>{t('ai.refreshModels')}</Text></TouchableOpacity>
              {canEdit && <>
                <TouchableOpacity disabled={busy || !models.some(m => m.key === selected && m.available)} style={[styles.primary, busy && styles.disabled]} onPress={() => void act('connect')}>
                  {busy ? <ActivityIndicator color="white" /> : <Text style={styles.primaryText}>{t('ai.connect')}</Text>}
                </TouchableOpacity>
                {status?.configured && <View style={{ flexDirection: rowDirection, gap: spacing.md }}>
                  <TouchableOpacity disabled={busy} style={styles.secondary} onPress={() => void act('test')}><Text style={styles.link}>{t('ai.test')}</Text></TouchableOpacity>
                  <TouchableOpacity disabled={busy} style={styles.secondary} onPress={() => void act('disconnect')}><Text style={styles.link}>{t('ai.disconnect')}</Text></TouchableOpacity>
                </View>}
              </>}
            </View>
            {status?.entitled && <View style={styles.card}>
              <Text style={[styles.heading, { textAlign }]}>{t('ai.knowledge.title')}</Text>
              <Text style={[styles.subtitle, { textAlign }]}>{t('ai.knowledge.description')}</Text>
              <TextInput multiline value={knowledge} maxLength={4000} onChangeText={value => { setKnowledge(value); setKnowledgeState('idle'); }}
                placeholder={t('ai.knowledge.placeholder')} placeholderTextColor={colors.text.secondary as string}
                style={[styles.knowledge, { textAlign }]} accessibilityLabel={t('ai.knowledge.title')} />
              <Text style={[styles.subtitle, { textAlign }]}>{knowledge.length} / 4000</Text>
              <TouchableOpacity disabled={knowledgeState === 'saving'} style={[styles.primary, knowledgeState === 'saving' && styles.disabled]} onPress={() => {
                setKnowledgeState('saving'); setError('');
                void apiService.saveAiKnowledge(knowledge).then(response => {
                  if (response.statusCode !== 1000) throw new Error(response.errors?.map((e: any) => typeof e === 'string' ? e : e.message).join(' ') || t('ai.actionFailed'));
                  setKnowledgeState('saved');
                }).catch((err: any) => { setKnowledgeState('idle'); setError(err.message || t('ai.actionFailed')); });
              }}>
                {knowledgeState === 'saving' ? <ActivityIndicator color="white" /> : <Text style={styles.primaryText}>{t(knowledgeState === 'saved' ? 'ai.knowledge.saved' : 'ai.knowledge.save')}</Text>}
              </TouchableOpacity>
            </View>}
            {test && <View style={styles.card}><Text style={styles.heading}>{t('ai.testSucceeded')}</Text><Text style={styles.body}>{test.content}</Text><Text style={styles.subtitle}>{t('ai.testTime', { seconds: (test.latencyMs / 1000).toFixed(2) })}</Text></View>}
            <Text style={[styles.subtitle, { textAlign }]}>{t('ai.pilotNote')}</Text>
            {!!routeParams?.onCompleteScreen && status?.configured && <TouchableOpacity style={styles.secondary} onPress={() => navigateTo(routeParams.onCompleteScreen, undefined, routeParams.onCompleteParams)}><Text style={styles.link}>{t('common.done')}</Text></TouchableOpacity>}
          </>}
        </>}
      </ScrollView>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  content: { padding: spacing.md, gap: spacing.md, maxWidth: 760, width: '100%', alignSelf: 'center', paddingBottom: spacing.xl },
  card: { padding: spacing.lg, borderRadius: 16, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, gap: spacing.md },
  heading: { color: colors.text.primary, fontFamily: typography.fontFamily.semibold, fontSize: typography.fontSize.base },
  body: { color: colors.text.primary, fontSize: typography.fontSize.sm },
  subtitle: { color: colors.text.secondary, fontSize: typography.fontSize.sm, lineHeight: 22 },
  model: { padding: spacing.md, borderRadius: 12, borderWidth: 1, borderColor: colors.border, alignItems: 'center', gap: spacing.md },
  selected: { borderColor: colors.primary },
  primary: { backgroundColor: colors.primary, padding: spacing.md, borderRadius: 10, alignItems: 'center' },
  primaryText: { color: 'white', fontFamily: typography.fontFamily.semibold },
  secondary: { paddingVertical: spacing.sm, alignItems: 'center' },
  link: { color: colors.primary, fontFamily: typography.fontFamily.medium },
  error: { color: colors.status.error, padding: spacing.sm },
  disabled: { opacity: 0.5 },
  knowledge: { minHeight: 160, padding: spacing.md, borderRadius: 10, borderWidth: 1, borderColor: colors.border, color: colors.text.primary, backgroundColor: colors.background.primary, textAlignVertical: 'top', fontSize: typography.fontSize.sm },
});
